import React, { useEffect, useMemo, useState } from 'react';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditCalendarOutlinedIcon from '@mui/icons-material/EditCalendarOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB, pl as plPL, ru as ruRU } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import { useBookingCopyTranslation as useTranslation } from '../i18n/bookingCopy';
import RoomBookingUserAvatar from './RoomBookingUserAvatar';
import TutorBookingChangeTimeComparison from './TutorBookingChangeTimeComparison';
import TutorLessonSessionTypeChip from './tutor/TutorLessonSessionTypeChip';
import { useThemeContext } from '../contexts/ThemeContext';
import { HostTutorBookingResponse } from '../models/Tutor';
import { tutorBookingStudentNames } from '../utils/tutorBookingStudentName';
import {
  calculateBookingDurationMinutes,
  formatTutorBookingScheduleLabels,
  serializeTutorBookingPickerDate,
  tutorBookingDisplayToPickerDate,
  tutorBookingDisplayToUtcIso,
} from '../utils/tutorBookingChangeProposal';
import { isTutorBookingModeratable } from '../utils/tutorBookingStatus';
import { bookingUtcToWallClock } from '../utils/bookingInstant';

export interface HostTutorBookingProposeChangeDialogProps {
  open: boolean;
  booking: HostTutorBookingResponse | null;
  submitting: boolean;
  perspective?: 'host' | 'tutor';
  onClose: () => void;
  onSubmit: (newStartTime: string, reason: string, autoAcceptTutorBooking: boolean) => Promise<void>;
}

function resolveDateFnsLocale(language: string): Locale {
  if (language.startsWith('pl')) return plPL;
  if (language.startsWith('ru')) return ruRU;
  return enGB;
}

function ScheduleSummaryCard({
  dateLabel,
  timeLabel,
  durationLabel,
}: {
  dateLabel: string;
  timeLabel: string;
  durationLabel: string;
}) {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        py: 1.5,
        px: 1.5,
        borderRadius: 1.5,
        bgcolor: 'action.hover',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
        <CalendarMonthOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.2 }} />
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.35 }}>
            {dateLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {timeLabel}
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
        <AccessTimeOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          {durationLabel}
        </Typography>
      </Stack>
    </Stack>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      sx={{
        display: 'block',
        color: 'text.secondary',
        letterSpacing: '0.08em',
        fontWeight: 700,
        mb: 1,
      }}
    >
      {children}
    </Typography>
  );
}

export default function HostTutorBookingProposeChangeDialog({
  open,
  booking,
  submitting,
  perspective = 'host',
  onClose,
  onSubmit,
}: HostTutorBookingProposeChangeDialogProps) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { mode } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [newStart, setNewStart] = useState<Date | null>(null);
  const [reason, setReason] = useState('');
  const [autoAcceptTutorBooking, setAutoAcceptTutorBooking] = useState(true);

  useEffect(() => {
    if (!open || !booking) return;
    setNewStart(tutorBookingDisplayToPickerDate(booking.startTime));
    setReason('');
    setAutoAcceptTutorBooking(true);
  }, [open, booking?.bookingId, booking?.startTime, booking?.timezone]);

  const showAutoAcceptOption = booking ? isTutorBookingModeratable(booking.status) : false;

  const originalStart = booking ? tutorBookingDisplayToPickerDate(booking.startTime) : null;
  const originalStartMs = originalStart?.getTime() ?? 0;
  const hasTimeChange = newStart != null && newStart.getTime() !== originalStartMs;
  const minDateTime = bookingUtcToWallClock(new Date(), booking?.timezone);
  const durationMinutes = booking
    ? calculateBookingDurationMinutes(booking.startTime, booking.endTime)
    : 60;

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours === 0) return `${remainingMinutes} ${t('roomBookingRequests.min')}`;
    if (remainingMinutes === 0) {
      return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')}`;
    }
    return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')} ${remainingMinutes} ${t('roomBookingRequests.min')}`;
  };

  const currentSchedule = useMemo(() => {
    if (!booking) return null;
    return formatTutorBookingScheduleLabels(
      booking.startTime,
      booking.endTime,
      i18n.language,
      formatDuration,
    );
  }, [booking, i18n.language, t]);

  const previewProposal = useMemo(() => {
    if (!booking || !newStart || !hasTimeChange) return null;
    const oldStartTime = tutorBookingDisplayToUtcIso(booking.startTime, booking.timezone);
    const newStartTime = serializeTutorBookingPickerDate(newStart, booking.timezone);
    const oldEndIso = tutorBookingDisplayToUtcIso(booking.endTime, booking.timezone);
    const newEndIso = serializeTutorBookingPickerDate(
      new Date(newStart.getTime() + durationMinutes * 60 * 1000),
      booking.timezone,
    );
    return { oldStartTime, newStartTime, oldEndIso, newEndIso };
  }, [booking, newStart, hasTimeChange, durationMinutes]);

  const handleSubmit = async () => {
    if (!newStart || !hasTimeChange || !booking) return;
    await onSubmit(
      serializeTutorBookingPickerDate(newStart, booking.timezone),
      reason.trim(),
      showAutoAcceptOption && autoAcceptTutorBooking,
    );
  };

  const studentNames = booking
    ? tutorBookingStudentNames({
        studentProfile: booking.studentProfile,
        userName: booking.userName,
      })
    : { primaryName: null, accountAlias: null };

  const primaryName = booking
    ? perspective === 'host'
      ? booking.tutorName?.trim() || t('hostTutorBookings.tutor', 'Tutor')
      : studentNames.primaryName || t('tutorBookings.user', 'Student')
    : '';

  const avatarImageUrl = booking
    ? perspective === 'host'
      ? booking.tutorProfileImageUrl
      : booking.userProfileImageUrl
    : null;

  const dialogPaperBg = mode === 'dark' ? '#1F2129' : '#FFFFFF';
  const dialogBorder = mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)';
  const fieldBg = mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa';
  const fieldHoverBg = mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f0f2ff';

  const outlinedFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: fieldBg,
      '&:hover': {
        backgroundColor: fieldHoverBg,
      },
    },
  };

  const hintText =
    showAutoAcceptOption && autoAcceptTutorBooking
      ? t(
          'hostTutorBookings.changeProposal.dialogHintAutoAccept',
          'The student must accept the new time. If they agree, the booking will be confirmed automatically at the new schedule.',
        )
      : t(
          'hostTutorBookings.changeProposal.dialogHint',
          'The student must accept the new time. Until then, the lesson stays at the current schedule.',
        );

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: { xs: 0, sm: 3 },
          margin: { xs: 0, sm: 2 },
          maxHeight: { xs: '100vh', sm: '90vh' },
          boxShadow:
            mode === 'dark' ? '0px 8px 32px rgba(0, 0, 0, 0.6)' : '0px 4px 20px rgba(0, 0, 0, 0.12)',
          background: dialogPaperBg,
          border: `1px solid ${dialogBorder}`,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.75, sm: 2 },
          borderBottom: '1px solid',
          borderColor: dialogBorder,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              bgcolor: alpha(theme.palette.primary.main, 0.14),
              color: 'primary.main',
            }}
          >
            <EditCalendarOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.25,
                letterSpacing: '-0.02em',
                color: mode === 'dark' ? '#ECECFB' : '#212529',
              }}
            >
              {t('hostTutorBookings.changeProposal.title', 'Propose a schedule change')}
            </Typography>
            {booking?.lessonOptionName?.trim() ? (
              <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25 }}>
                {booking.lessonOptionName.trim()}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        <IconButton
          aria-label={t('common.close', 'Close')}
          onClick={onClose}
          disabled={submitting}
          sx={{ mt: -0.5, mr: -0.5 }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          bgcolor: dialogPaperBg,
        }}
      >
        <Stack spacing={2.5}>
          {booking ? (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="flex-start"
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.background.paper, 0.6),
              }}
            >
              <RoomBookingUserAvatar
                userProfileImageUrl={avatarImageUrl}
                displayName={primaryName}
                size={52}
              />
              <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                  {primaryName}
                </Typography>
                {perspective === 'host' && studentNames.primaryName ? (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <PersonOutlineOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {t('hostTutorBookings.changeProposal.forStudent', 'Student: {{name}}', {
                        name: studentNames.primaryName,
                      })}
                    </Typography>
                  </Stack>
                ) : null}
                {booking.spaceName?.trim() ? (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <PlaceOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {booking.spaceName.trim()}
                    </Typography>
                  </Stack>
                ) : null}
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {booking.lessonSessionType ? (
                    <TutorLessonSessionTypeChip sessionType={booking.lessonSessionType} />
                  ) : null}
                </Stack>
              </Stack>
            </Stack>
          ) : null}

          {currentSchedule ? (
            <Box>
              <SectionLabel>
                {t('hostTutorBookings.changeProposal.currentLessonTime', 'Current lesson time')}
              </SectionLabel>
              <ScheduleSummaryCard {...currentSchedule} />
            </Box>
          ) : null}

          <Box
            sx={{
              display: 'flex',
              gap: 1.25,
              p: 1.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.08),
              border: '1px solid',
              borderColor: alpha(theme.palette.info.main, 0.28),
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 20, color: 'info.main', mt: 0.1, flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {hintText}
            </Typography>
          </Box>

          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: hasTimeChange ? 'primary.main' : 'divider',
              bgcolor: hasTimeChange
                ? alpha(theme.palette.primary.main, 0.06)
                : alpha(theme.palette.background.paper, 0.45),
              transition: 'border-color 0.2s ease, background-color 0.2s ease',
            }}
          >
            <SectionLabel>
              {t('hostTutorBookings.changeProposal.newLessonTime', 'New lesson start')}
            </SectionLabel>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={resolveDateFnsLocale(i18n.language)}
            >
              <DateTimePicker
                value={newStart}
                onChange={(value) => setNewStart(value)}
                ampm={false}
                minDateTime={minDateTime}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    sx: outlinedFieldSx,
                    helperText: t(
                      'hostTutorBookings.changeProposal.durationKept',
                      'Lesson length stays {{minutes}} minutes.',
                      { minutes: durationMinutes },
                    ),
                  },
                }}
              />
            </LocalizationProvider>

            {!hasTimeChange && newStart ? (
              <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
                {t(
                  'hostTutorBookings.changeProposal.pickDifferentTime',
                  'Choose a different start time to continue.',
                )}
              </Typography>
            ) : null}
          </Box>

          {previewProposal ? (
            <Box>
              <SectionLabel>
                {t('hostTutorBookings.changeProposal.preview', 'What the student will see')}
              </SectionLabel>
              <TutorBookingChangeTimeComparison
                oldStartIso={previewProposal.oldStartTime}
                oldEndIso={previewProposal.oldEndIso}
                newStartIso={previewProposal.newStartTime}
                newEndIso={previewProposal.newEndIso}
                locale={i18n.language}
                timezone={booking?.timezone}
                compact
              />
            </Box>
          ) : null}

          <Box>
            <SectionLabel>
              {t('hostTutorBookings.changeProposal.messageLabel', 'Message to student (optional)')}
            </SectionLabel>
            <TextField
              fullWidth
              multiline
              minRows={3}
              placeholder={t(
                'hostTutorBookings.changeProposal.reasonPlaceholder',
                'Explain why you need to reschedule…',
              )}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={outlinedFieldSx}
            />
          </Box>

          {showAutoAcceptOption ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: autoAcceptTutorBooking
                  ? alpha(theme.palette.primary.main, 0.35)
                  : 'divider',
                bgcolor: autoAcceptTutorBooking
                  ? alpha(theme.palette.primary.main, 0.06)
                  : 'transparent',
              }}
            >
              <FormControlLabel
                sx={{
                  m: 0,
                  alignItems: 'flex-start',
                  '& .MuiCheckbox-root': {
                    py: 0,
                    pl: 0,
                    pr: 1,
                    mt: 0.1,
                  },
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    lineHeight: 1.45,
                    color: 'text.secondary',
                  },
                }}
                control={
                  <Checkbox
                    checked={autoAcceptTutorBooking}
                    onChange={(e) => setAutoAcceptTutorBooking(e.target.checked)}
                  />
                }
                label={t(
                  'hostTutorBookings.changeProposal.autoAcceptTutorBooking',
                  'Automatically confirm booking if the student accepts this change',
                )}
              />
            </Box>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          pt: 0,
          gap: 1,
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          borderTop: '1px solid',
          borderColor: dialogBorder,
          bgcolor: dialogPaperBg,
          '& .MuiButton-root': {
            m: 0,
            width: { xs: '100%', sm: 'auto' },
          },
        }}
      >
        <Button
          onClick={onClose}
          disabled={submitting}
          variant="outlined"
          color="inherit"
          sx={{
            borderRadius: 999,
            px: 2.5,
            borderColor: dialogBorder,
          }}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !hasTimeChange}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />
          }
          sx={{
            borderRadius: 999,
            px: 3,
            fontWeight: 700,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          {t('hostTutorBookings.changeProposal.sendProposal', 'Send to student')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
